import { FC, useState } from 'react';
import styles from './styles.module.scss';
import { Icon } from '@components/icons';
import Input from '@components/common/input';
import Select from '@components/common/select';
import Textarea from '@components/common/textarea';
import Uploader from '@components/uploader';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiSupportService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import useModalContext from '@hooks/context/useModalContext';
import { TCreateRequest } from '@xeppt/xeppt-sdk/types';

interface ReportProblemSubviewProps {
    // Human-friendly transaction alias (TXN-XXXX-XXXX), never the raw UUID —
    // customers only ever see the alias; support finds it via admin search,
    // which matches both the alias and the raw id.
    transactionRef: string;
    isRefund?: boolean;
    title?: string;
    onBack: () => void;
    onClose: () => void;
}

const ReportProblemSubview: FC<ReportProblemSubviewProps> = ({
    transactionRef,
    isRefund,
    title = 'Report problem',
    onBack,
    onClose
}) => {
    const { placeholderLocale, labelLocale, requestErrorLocale, requestSuccessLocale } =
        useLocales();
    const { onOpen, onClose: closeModal } = useModalContext();
    const defaultSubject = isRefund
        ? `Refund Transaction ${transactionRef}`
        : `Transaction ${transactionRef}`;
    const [subject, setSubject] = useState(defaultSubject);
    const [comment, setComment] = useState('');
    const [reason, setReason] = useState('');
    const [uploads, setUploads] = useState<FormData[]>([]);
    const isSubmitDisabled = !subject || !comment || !reason;
    const { data: reasons } = useApiQuery({
        method: () => apiSupportService.getReasons()
    });
    const { handleRequest: handleUploadAttachment } = useApiMutation({
        method: (payload: FormData) => apiSupportService.uploadAttachment(payload)
    });
    const { handleRequest: handleCreateRequest, isLoading } = useApiMutation({
        method: (payload: TCreateRequest) => apiSupportService.createTicket(payload),
        onSuccess: () => {
            requestSuccessLocale('create_request');
            onOpen({
                // Literal id (not the modalIds map) on purpose: importing @const/modals
                // here drags the whole modal registry — incl. usehooks-ts (ESM) — into
                // Jest and breaks the view_transaction test parse. This id is stable.
                modalId: 'SUCCESS_SUBMIT_REQUEST',
                onSubmit: closeModal
            });
        },
        onError: requestErrorLocale
    });
    const handleSubmit = () => {
        if (uploads.length > 0) {
            Promise.all(uploads.map((item) => handleUploadAttachment(item))).then((data) => {
                handleCreateRequest({
                    comment,
                    subject,
                    reason,
                    // @ts-ignore
                    uploads: data.map((item) => item?.token)
                });
            });
        } else {
            handleCreateRequest({ comment, subject, reason, uploads: [] });
        }
    };
    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <button type="button" className={styles.back_btn} onClick={onBack}>
                    <Icon name="arrow_left" width={24} height={24} />
                </button>
                <Typography fz={24} fw={700} alignment="center">
                    {title}
                </Typography>
                <button type="button" className={styles.close_btn} onClick={onClose}>
                    <Icon name="close" width={24} height={24} />
                </button>
            </div>
            <div className={styles.form}>
                <Input
                    onChange={(val) => setSubject(val as string)}
                    value={subject}
                    label={labelLocale('subject')}
                    full
                    placeholder={placeholderLocale('enter_subject_request')}
                />
                <Select
                    items={
                        reasons?.map((item) => ({ label: item.name, value: item.value })) || []
                    }
                    onChange={(val) => setReason(val as string)}
                    value={reason}
                    label={labelLocale('reason')}
                    full
                    placeholder={placeholderLocale('select_reason_request')}
                />
                <Textarea
                    full
                    label={labelLocale('description')}
                    placeholder={placeholderLocale('describe_issue')}
                    onChange={(val) => setComment(val)}
                    value={comment}
                />
                <Uploader
                    full
                    size={10}
                    accept={['.doc', 'image/png', 'image/jpg', 'image/jpeg', '.pdf', '.docx']}
                    label={
                        <div className={styles.attachment_label}>
                            <Icon name="attach" /> Attach file (optional)
                        </div>
                    }
                    onChange={(files) => setUploads(files)}
                />
            </div>
            <div className={styles.submit_wrapper}>
                <Button
                    variant="primary"
                    size="medium"
                    disabled={isSubmitDisabled}
                    isLoading={isLoading}
                    onClick={handleSubmit}>
                    Submit
                </Button>
            </div>
        </div>
    );
};

export default ReportProblemSubview;
